import { useEffect, useRef } from "react";

// Cloudflare Turnstile before creating a restaurant (server: src/worker/lib/turnstile.ts). The widget shows itself
// only when Cloudflare needs an interaction ("interaction-only"); otherwise nothing changes on the page.

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const WAIT_MS = 60_000;

let loading: Promise<void> | null = null;
function loadScript(): Promise<void> {
  loading ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loading = null;
      s.remove();
      reject(new Error("turnstile_unavailable"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export class ChallengeError extends Error {}

/**
 * Renders the challenge into `ref` when a site key is given; `take()` resolves with a fresh single-use token (or
 * undefined when no challenge is configured) and prepares the next one.
 */
export function useTurnstile(siteKey: string | null | undefined) {
  const ref = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const token = useRef<string | null>(null);
  const waiters = useRef<((t: string) => void)[]>([]);

  useEffect(() => {
    if (!siteKey) return;
    let live = true;
    loadScript()
      .then(() => {
        if (!live || !ref.current || !window.turnstile) return;
        widget.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          appearance: "interaction-only",
          language: "ro",
          callback: (t: string) => {
            token.current = t;
            for (const w of waiters.current.splice(0)) w(t);
          },
          "expired-callback": () => {
            token.current = null;
          },
          "error-callback": () => {
            token.current = null;
          },
        });
      })
      .catch(() => undefined);
    return () => {
      live = false;
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey]);

  async function take(): Promise<string | undefined> {
    if (!siteKey) return undefined;
    const t =
      token.current ??
      (await new Promise<string>((resolve, reject) => {
        waiters.current.push(resolve);
        setTimeout(
          () =>
            reject(
              new ChallengeError(
                "Verificarea de securitate nu s-a încărcat. Verifică conexiunea și încearcă din nou.",
              ),
            ),
          WAIT_MS,
        );
      }));
    token.current = null;
    if (widget.current) window.turnstile?.reset(widget.current);
    return t;
  }

  return { ref, take };
}
